import { useState, useCallback, useRef } from 'react';
import api from '../utils/api';
import { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import type { AxiosInstance } from 'axios';

interface UseApiReturn<T> {
  data: T | null;
  error: AxiosError | null;
  loading: boolean;
  request: (config: AxiosRequestConfig) => Promise<T>;
}

/**
 * Generic hook for making API requests using the configured axios instance.
 */
export function useApi<T = any>(client: AxiosInstance = api): UseApiReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<AxiosError | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const latestRequestRef = useRef(0);
  const pendingRequestsRef = useRef(0);

  const request = useCallback(async (config: AxiosRequestConfig): Promise<T> => {
    const requestId = ++latestRequestRef.current;
    pendingRequestsRef.current += 1;
    setLoading(true);
    setError(null);
    try {
      const response: AxiosResponse<T> = await client(config);
      if (latestRequestRef.current === requestId) setData(response.data);
      return response.data;
    } catch (err) {
      const axiosError = err as AxiosError;
      if (latestRequestRef.current === requestId) setError(axiosError);
      throw axiosError;
    } finally {
      pendingRequestsRef.current -= 1;
      if (pendingRequestsRef.current === 0) setLoading(false);
    }
  }, [client]);

  return { data, error, loading, request };
}

export default useApi;
